import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import styled from 'styled-components'

import { ProjectFloor } from '../../../../../api/types'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { media } from '../../../../../styles/breakpoints'

import { ScanHealthFloorSection } from './ScanHealthFloorSection'
import {
  DesktopLeftRail,
  MobileLeftRail,
  ScanHealthLeftRail,
} from './ScanHealthLeftRail'
import {
  DesktopRightRail,
  MobileRightRail,
  ScanHealthRightRail,
} from './ScanHealthRightRail'
import { useScrollSyncedFloor } from './useScrollSyncedFloor'

export const ManageScanHealth = () => {
  const { state: buildingState } = useBuildingContext()
  const floors = useMemo<ProjectFloor[]>(
    () => buildingState.projectData?.floors ?? [],
    [buildingState.projectData?.floors],
  )
  const projectId = buildingState.projectId

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const pageLayoutRef = useRef<HTMLDivElement | null>(null)
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null)

  const setScrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node
    setScrollRoot(node)
  }, [])

  const [selectorExpanded, setSelectorExpanded] = useState(true)
  const [hasInitializedFloors, setHasInitializedFloors] = useState(false)
  const [selectedFloorCodes, setSelectedFloorCodes] = useState<Set<string>>(
    () => new Set(),
  )

  useEffect(() => {
    if (floors.length && !hasInitializedFloors) {
      setSelectedFloorCodes(new Set(floors.map(f => f.floor_code)))
      setHasInitializedFloors(true)
    }
  }, [floors, hasInitializedFloors])

  const onToggleFloor = useCallback((floorCode: string, checked: boolean) => {
    setSelectedFloorCodes(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(floorCode)
      } else {
        next.delete(floorCode)
      }
      return next
    })
  }, [])

  const onSelectAll = useCallback(() => {
    setSelectedFloorCodes(new Set(floors.map(f => f.floor_code)))
  }, [floors])

  const onDeselectAll = useCallback(() => {
    setSelectedFloorCodes(new Set())
  }, [])

  const visibleFloors = useMemo(
    () =>
      floors
        .filter(f => selectedFloorCodes.has(f.floor_code))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [floors, selectedFloorCodes],
  )

  const { registerSection } = useScrollSyncedFloor(
    selectedFloorCodes,
    scrollRoot,
  )

  useEffect(() => {
    const layout = pageLayoutRef.current
    if (!layout) return

    const onWheel = (event: WheelEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (!layout.contains(target)) return

      if (target.closest('[data-scan-health-map]')) return
      if (target.closest('[data-scan-health-selector]')) return

      const scrollEl = scrollContainerRef.current
      if (!scrollEl) return

      if (scrollEl.contains(target)) return

      event.preventDefault()
      scrollEl.scrollBy({ top: event.deltaY })
    }

    document.addEventListener('wheel', onWheel, { passive: false })
    return () => document.removeEventListener('wheel', onWheel)
  }, [])

  const leftRailProps = {
    floors,
    selectedFloorCodes,
    onToggleFloor,
    onSelectAll,
    onDeselectAll,
    expanded: selectorExpanded,
    setExpanded: setSelectorExpanded,
  }

  return (
    <PageLayout ref={pageLayoutRef} data-scan-health-page>
      <DesktopLeftRail>
        <ScanHealthLeftRail {...leftRailProps} />
      </DesktopLeftRail>

      <DesktopRightRail>
        <ScanHealthRightRail />
      </DesktopRightRail>

      <ContentColumn ref={setScrollContainerRef}>
        <MobileLeftRail>
          <ScanHealthLeftRail {...leftRailProps} />
        </MobileLeftRail>

        <MobileRightRail>
          <ScanHealthRightRail />
        </MobileRightRail>

        {visibleFloors.length === 0 && (
          <EmptyMessage>
            Select at least one floor to view scan health.
          </EmptyMessage>
        )}

        {visibleFloors.map(floor => (
          <ScanHealthFloorSection
            key={floor.floor_code}
            floor={floor}
            projectId={projectId}
            registerSection={registerSection}
            scrollRoot={scrollRoot}
          />
        ))}
      </ContentColumn>
    </PageLayout>
  )
}

const PageLayout = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
  padding: 0 24px 24px;
`

const ContentColumn = styled.div`
  margin-left: 280px;
  margin-right: 248px;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding-top: 8px;
  box-sizing: border-box;

  @media ${media.belowTablet} {
    margin-left: 0;
    margin-right: 0;
    padding-top: 0;
  }
`

const EmptyMessage = styled.p`
  color: #686868;
  font-size: 14px;
`
